/**
 * DESIGN TOKENS — Single source of truth for the entire visual system.
 *
 * WHY CSS custom properties instead of JS constants?
 * 1. Performance: no JS runtime cost, browser handles inheritance
 * 2. DevTools: inspect & tweak tokens live in the browser
 * 3. Cascade: every styled-component automatically inherits updates
 */
import { createGlobalStyle } from "styled-components";

export const DesignTokens = createGlobalStyle`
  /* ── Light mode (default) ─────────────────────────────────────── */
  :root {
    /* Brand */
    --color-primary: #D81B60;
    --color-primary-hover: #B0124A;
    --color-primary-active: #8A0E3A;
    --color-primary-light: #FDF0F4;
    --color-primary-lighter: #FBD9E3;
    --color-secondary: #FF7F6B;
    --color-gradient: linear-gradient(135deg, #D81B60 0%, #E4485C 100%);
    --color-gradient-hover: linear-gradient(135deg, #B0124A 0%, #D0384F 100%);

    /* Surfaces */
    --color-bg: #FAFAFA;
    --color-bg-elevated: #FFFFFF;
    --color-bg-subtle: #F8FAFC;
    --color-bg-muted: #F1F5F9;
    --color-bg-overlay: rgba(15, 23, 42, 0.4);

    /* Text */
    --color-text: #0F172A;
    --color-text-secondary: #64748B;
    --color-text-tertiary: #94A3B8;
    --color-text-on-primary: #FFFFFF;
    --color-text-link: #D81B60;

    /* Borders */
    --color-border: #E2E8F0;
    --color-border-light: #F1F5F9;
    --color-border-accent: #FDF0F4;

    /* Shadows — layered for depth perception */
    --shadow-xs: 0 1px 2px rgba(0, 0, 0, 0.04);
    --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
    --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.07), 0 2px 4px -2px rgba(0, 0, 0, 0.05);
    --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.04);
    --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04);
    --shadow-primary: 0 4px 14px rgba(216, 27, 96, 0.25);
    --shadow-primary-lg: 0 8px 24px rgba(216, 27, 96, 0.3);

    /* Typography — Inter for body, Plus Jakarta Sans for display */
    --font-display: 'Plus Jakarta Sans', 'Inter', system-ui, -apple-system, sans-serif;
    --font-body: 'Inter', system-ui, -apple-system, sans-serif;

    /* Font sizes — clamp() for fluid scaling */
    --text-xs: 0.75rem;     /* 12px */
    --text-sm: 0.875rem;    /* 14px */
    --text-base: 1rem;      /* 16px */
    --text-lg: 1.125rem;    /* 18px */
    --text-xl: 1.25rem;     /* 20px */
    --text-2xl: 1.5rem;     /* 24px */
    --text-3xl: clamp(1.75rem, 2.5vw + 0.5rem, 2.25rem);
    --text-4xl: clamp(2rem, 3vw + 0.5rem, 3rem);
    --text-hero: clamp(2.25rem, 4vw + 0.5rem, 3.5rem);

    /* Font weights */
    --weight-normal: 400;
    --weight-medium: 500;
    --weight-semibold: 600;
    --weight-bold: 700;
    --weight-extrabold: 800;

    /* Line heights */
    --leading-tight: 1.15;
    --leading-snug: 1.3;
    --leading-normal: 1.5;
    --leading-relaxed: 1.65;

    /* Letter spacing */
    --tracking-tight: -0.025em;
    --tracking-normal: 0;
    --tracking-wide: 0.025em;

    /* Spacing — 4px base scale */
    --space-1: 4px;
    --space-2: 8px;
    --space-3: 12px;
    --space-4: 16px;
    --space-5: 20px;
    --space-6: 24px;
    --space-8: 32px;
    --space-10: 40px;
    --space-12: 48px;
    --space-16: 64px;
    --space-20: 80px;
    --space-section: clamp(48px, 8vw, 80px);

    /* Border radius */
    --radius-sm: 8px;
    --radius-md: 12px;
    --radius-lg: 16px;
    --radius-xl: 20px;
    --radius-2xl: 24px;
    --radius-full: 9999px;

    /* Transitions */
    --ease-out: cubic-bezier(0.16, 1, 0.3, 1);
    --ease-in-out: cubic-bezier(0.65, 0, 0.35, 1);
    --duration-fast: 150ms;
    --duration-normal: 200ms;
    --duration-slow: 300ms;
    --transition-colors: color var(--duration-normal) var(--ease-out),
                         background-color var(--duration-normal) var(--ease-out),
                         border-color var(--duration-normal) var(--ease-out);
    --transition-transform: transform var(--duration-normal) var(--ease-out);
    --transition-shadow: box-shadow var(--duration-normal) var(--ease-out);
    --transition-all: all var(--duration-normal) var(--ease-out);

    /* Layout */
    --header-height: 64px;
    --content-max-width: 1000px;
    --content-narrow: 700px;

    /* Z-index scale */
    --z-base: 0;
    --z-dropdown: 100;
    --z-sticky: 500;
    --z-overlay: 900;
    --z-modal: 1000;
    --z-toast: 1100;

    /* Frosted glass */
    --glass-bg: rgba(255, 255, 255, 0.8);
    --glass-blur: blur(16px);
    --glass-border: rgba(255, 255, 255, 0.2);
  }

  /* ── DatePicker dropdown — brand-consistent calendar ──────── */
  .ant-picker-dropdown {
    width: 100%;
  }

  .ant-picker-dropdown .ant-picker-panel-container,
  .ant-picker-dropdown .ant-picker-panel,
  .ant-picker-dropdown .ant-picker-date-panel,
  .ant-picker-dropdown .ant-picker-content {
    width: 100% !important;
  }

  .ant-picker-dropdown .ant-picker-panel-container {
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    border: 1px solid var(--color-border);
    overflow: hidden;
  }

  .ant-picker-dropdown .ant-picker-panel {
    border-radius: var(--radius-lg);
    border: none;
  }

  .ant-picker-dropdown .ant-picker-header {
    padding: 10px 16px;
    border-bottom: 1px solid var(--color-border-light);
  }

  .ant-picker-dropdown .ant-picker-body {
    padding: 12px 16px 16px;
  }

  .ant-picker-dropdown .ant-picker-content th,
  .ant-picker-dropdown .ant-picker-content td {
    width: calc(100% / 7);
  }

  .ant-picker-dropdown .ant-picker-cell-inner {
    border-radius: 50% !important;
    height: 36px !important;
    width: 36px !important;
    line-height: 36px !important;
    font-weight: var(--weight-medium);
    transition: var(--transition-colors);
  }

  .ant-picker-dropdown
    .ant-picker-cell:not(.ant-picker-cell-disabled):hover
    .ant-picker-cell-inner {
    background: var(--color-primary-light) !important;
  }

  .ant-picker-dropdown .ant-picker-cell-selected .ant-picker-cell-inner,
  .ant-picker-dropdown .ant-picker-cell-selected:hover .ant-picker-cell-inner {
    background: var(--color-gradient) !important;
    color: var(--color-text-on-primary) !important;
    font-weight: var(--weight-semibold);
    box-shadow: var(--shadow-primary);
  }

  .ant-picker-dropdown
    .ant-picker-cell-today
    .ant-picker-cell-inner::before {
    border-color: var(--color-primary) !important;
    border-radius: 50% !important;
  }

  /* ── Mobile full-screen drawers (Instagram-style) ──────────── */
  /* All Ant Design drawers go full-width on mobile.
     Clean close button (X) is provided by Ant's closable prop. */
  @media (max-width: 767px) {
    .ant-drawer-right .ant-drawer-content-wrapper {
      width: 100% !important;
      max-width: 100% !important;
    }

    .ant-drawer .ant-drawer-header {
      padding: var(--space-4) var(--space-4) var(--space-3);
    }

    .ant-drawer .ant-drawer-close {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--radius-full);
      transition: var(--transition-colors);
    }

    .ant-drawer .ant-drawer-close:hover {
      background: var(--color-bg-muted);
    }
  }
`;
